import { Directive, ElementRef, AfterViewInit, HostListener, OnDestroy, NgZone } from '@angular/core';

declare var window: any;

@Directive({
  selector: '[appCompanyInfoScreenCalculator]'
})
export class CompanyInfoScreenCalculationDirective implements AfterViewInit, OnDestroy {
  totalHeight: number;
  private resizeTimeout: any;
  private focusedElement: HTMLElement | null = null;
  private isKeyboardOpen = false;
  private originalHeight: number;
  private scrollTimeout: any;
  
  // Store bound event handlers to properly remove them
  private boundKeyboardShow = () => this.onKeyboardShow();
  private boundKeyboardHide = () => this.onKeyboardHide();

  constructor(
    private el: ElementRef,
    private zone: NgZone
  ) {
    this.originalHeight = window.innerHeight;
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.calculateOffset();
      this.setupInputFocusListeners();
      this.setupKeyboardListeners();
    }, 100);
  }

  ngOnDestroy() {
    this.removeInputFocusListeners();
    this.removeKeyboardListeners();
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
  }

  setupKeyboardListeners() {
    // Use bound methods for proper cleanup
    window.addEventListener('keyboardWillShow', this.boundKeyboardShow);
    window.addEventListener('keyboardDidShow', this.boundKeyboardShow);
    window.addEventListener('keyboardWillHide', this.boundKeyboardHide);
    window.addEventListener('keyboardDidHide', this.boundKeyboardHide);
    window.addEventListener('native.keyboardshow', this.boundKeyboardShow);
    window.addEventListener('native.keyboardhide', this.boundKeyboardHide);
  }

  removeKeyboardListeners() {
    // Now this will actually work
    window.removeEventListener('keyboardWillShow', this.boundKeyboardShow);
    window.removeEventListener('keyboardDidShow', this.boundKeyboardShow);
    window.removeEventListener('keyboardWillHide', this.boundKeyboardHide);
    window.removeEventListener('keyboardDidHide', this.boundKeyboardHide);
    window.removeEventListener('native.keyboardshow', this.boundKeyboardShow);
    window.removeEventListener('native.keyboardhide', this.boundKeyboardHide);
  }

  onKeyboardShow() {
    this.zone.run(() => {
      this.isKeyboardOpen = true;
      
      if (this.scrollTimeout) {
        clearTimeout(this.scrollTimeout);
      }
      
      if (this.focusedElement) {
        this.scrollTimeout = setTimeout(() => {
          this.scrollToElement(this.focusedElement);
        }, 350); // Slightly longer delay for stability
      }
    });
  }

  onKeyboardHide() {
    this.zone.run(() => {
      this.isKeyboardOpen = false;
      
      // Clear any pending scrolls
      if (this.scrollTimeout) {
        clearTimeout(this.scrollTimeout);
      }
      
      setTimeout(() => {
        this.calculateOffset();
      }, 100);
    });
  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }

    const currentHeight = window.innerHeight;
    const heightDiff = this.originalHeight - currentHeight;
    
    // Update keyboard state based on viewport change
    const wasKeyboardOpen = this.isKeyboardOpen;
    
    if (heightDiff > 150) {
      this.isKeyboardOpen = true;
    } else if (heightDiff < 50) {
      this.isKeyboardOpen = false;
    }

    // If keyboard just opened and we have a focused element, scroll
    if (!wasKeyboardOpen && this.isKeyboardOpen && this.focusedElement) {
      if (this.scrollTimeout) {
        clearTimeout(this.scrollTimeout);
      }
      this.scrollTimeout = setTimeout(() => {
        this.scrollToElement(this.focusedElement);
      }, 200);
    }

    this.resizeTimeout = setTimeout(() => {
      this.calculateOffset();
    }, 150);
  }

  setupInputFocusListeners() {
    const inputs = this.el.nativeElement.querySelectorAll('ion-input, ion-select, input, select, textarea');
    
    inputs.forEach((ionElement: HTMLElement) => {
      let nativeInput = ionElement;
      if (ionElement.tagName === 'ION-INPUT') {
        const innerInput = ionElement.querySelector('input');
        if (innerInput) {
          nativeInput = innerInput;
        }
      }

      // Store element reference in dataset for cleanup
      nativeInput.dataset.focusHandler = 'true';
      
      nativeInput.addEventListener('focus', () => {
        this.onInputFocus(ionElement);
      }, { passive: true });

      nativeInput.addEventListener('blur', () => {
        this.onInputBlur();
      }, { passive: true });
    });
  }

  removeInputFocusListeners() {
    const inputs = this.el.nativeElement.querySelectorAll('[data-focus-handler="true"]');
    inputs.forEach((input: HTMLElement) => {
      input.removeEventListener('focus', () => {});
      input.removeEventListener('blur', () => {});
    });
  }

  onInputFocus(element: HTMLElement) {
    this.focusedElement = element;
    
    // Don't scroll immediately on focus - wait for keyboard
    // The keyboard event or resize event will trigger the scroll
  }

  onInputBlur() {
    // Delay clearing to handle quick focus switches
    setTimeout(() => {
      this.focusedElement = null;
    }, 200);
  }

  scrollToElement(element: HTMLElement) {
  if (!element) return;

  try {
    // Find the parent page's ion-content (will work from child component)
    const ionContent = element.closest('ion-content') || document.querySelector('ion-content');
    
    if (ionContent) {
      ionContent.getScrollElement().then((scrollElement: HTMLElement) => {
        const formItem = element.closest('ion-item') || element.closest('.form-item') || element;
        const rect = formItem.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const keyboardHeight = this.originalHeight - viewportHeight;
        
        const visibleBottom = viewportHeight - keyboardHeight;
        const hiddenAmount = rect.bottom - visibleBottom;

        if (hiddenAmount > -50) {
          const scrollTop = scrollElement.scrollTop;
          const targetScroll = scrollTop + hiddenAmount + 120;
          
          ionContent.scrollToPoint(0, Math.max(0, targetScroll), 300);
        }
      });
    }
  } catch (error) {
    console.warn('Scroll error:', error);
  }
}

  calculateOffset() {
    const headingWrapper = this.el.nativeElement.querySelector('.heading-wrapper');
    const formWrapper = this.el.nativeElement.querySelector('.form-wrapper');

    if (!headingWrapper || !formWrapper) {
      setTimeout(() => this.calculateOffset(), 50);
      return;
    }

    this.totalHeight = this.el.nativeElement.offsetHeight;
    const innerHeight = headingWrapper.offsetHeight;
    const top = headingWrapper.offsetTop;
    
    if (this.totalHeight > 0 && innerHeight > 0) {
      const minHeight = this.totalHeight - innerHeight - top;
      formWrapper.style.minHeight = `${minHeight}px`;
    } else {
      setTimeout(() => this.calculateOffset(), 50);
    }
  }
}